import { useState } from "react";
import axios from "axios";
import { company } from "@/data/site";
import { FadeUp } from "@/components/motion/Reveal";
import { TerminDialog } from "@/components/TerminDialog";
import { cn } from "@/lib/utils";

const routeHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(company.mapsQuery)}`;
const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(company.mapsQuery)}&output=embed`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ k, label, value, error, onChange, as = "input", ...rest }) => {
  const Comp = as;
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <Comp value={value} onChange={(e) => onChange(k, e.target.value)} aria-invalid={!!error} data-testid={`contact-input-${k}`} className={cn("field mt-1", as === "textarea" && "resize-none", error && "field-error")} {...rest} />
      {error && <span role="alert" className="block mt-1.5 text-[13px] text-[#ef6a6a]" data-testid={`contact-error-${k}`}>{error}</span>}
    </label>
  );
};

const ContactForm = () => {
  const [d, setD] = useState({ name: "", contact: "", message: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const set = (k, v) => { setD((s) => ({ ...s, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (d.name.trim().length < 2) err.name = "Bitte Namen angeben.";
    if (d.contact.trim().length < 5) err.contact = "Bitte Telefonnummer oder E-Mail angeben.";
    if (d.message.trim().length < 5) err.message = "Bitte Ihr Anliegen kurz beschreiben.";
    if (Object.keys(err).length) { setErrors(err); return; }
    setState("busy");
    try {
      await axios.post(`${API}/contact`, d);
      const key = process.env.REACT_APP_WEB3FORMS_KEY;
      if (key) {
        const fd = new FormData();
        fd.append("access_key", key);
        fd.append("subject", `Kontaktanfrage – ${d.name}`);
        fd.append("from_name", "NIVO GARAGE Website");
        fd.append("name", d.name);
        fd.append("Kontakt", d.contact);
        fd.append("Anliegen", d.message);
        fetch("https://api.web3forms.com/submit", { method: "POST", body: fd }).catch(() => {});
      }
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="panel p-7" role="status" data-testid="contact-form-success">
        <p className="h-sub">Danke, {d.name.split(" ")[0]}.</p>
        <p className="mt-3 text-[color:var(--ink-2)]">Ihre Nachricht ist bei uns eingegangen. Wir melden uns persönlich bei Ihnen.</p>
      </div>
    );
  }

  const f = (p) => <Field value={d[p.k]} error={errors[p.k]} onChange={set} {...p} />;

  return (
    <form onSubmit={submit} noValidate className="panel p-7 space-y-7" data-testid="contact-form">
      <div>
        <p className="h-sub">Kurze Anfrage</p>
        <p className="mt-2 text-[15px] text-[color:var(--ink-2)]">Lieber direkt? Mit <TerminDialog testId="contact-note-termin" className="text-[color:var(--violet)] link-underline">Termin anfragen</TerminDialog> erreichen Sie uns per Telefon, WhatsApp oder E-Mail.</p>
      </div>
      {f({ k: "name", label: "Name", placeholder: "Vor- und Nachname", autoComplete: "name" })}
      {f({ k: "contact", label: "Telefon oder E-Mail", placeholder: "+41 … oder name@beispiel.ch", autoComplete: "tel" })}
      {f({ k: "message", label: "Anliegen", as: "textarea", rows: 4, placeholder: "Worum geht es?" })}
      {state === "error" && <p role="alert" className="text-[14px] text-[#ef6a6a]" data-testid="contact-form-error">Die Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder rufen Sie uns an.</p>}
      <button type="submit" disabled={state === "busy"} data-testid="contact-form-submit" className="btn-violet disabled:opacity-60">{state === "busy" ? "Wird gesendet…" : "Nachricht senden"}</button>
    </form>
  );
};

export const Contact = () => (
  <section id="kontakt" data-testid="contact-section" className="pb-16 md:pb-24">
    <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5 space-y-10">
        {company.confirmed.address ? (
          <FadeUp>
            <p className="eyebrow mb-3">Adresse</p>
            <address className="not-italic text-[20px] md:text-[22px] font-medium tracking-[-0.01em] leading-snug">
              {company.legalName}<br />{company.street}<br />{company.zip} {company.city}
            </address>
            <a href={routeHref} target="_blank" rel="noreferrer" data-testid="contact-route-button" className="btn-outline mt-5 !min-h-[44px] !py-2.5">Route öffnen</a>
          </FadeUp>
        ) : (
          <FadeUp>
            <p className="eyebrow mb-3">Standort</p>
            <p className="text-[20px] md:text-[22px] font-medium tracking-[-0.01em] leading-snug" data-testid="contact-address-pending">{company.legalName}<br />{company.region}</p>
            <p className="mt-3 text-[15px] text-[color:var(--ink-2)]">Die genaue Werkstattadresse geben wir bei der Terminbestätigung bekannt.</p>
          </FadeUp>
        )}
        <FadeUp delay={0.05}>
          <p className="eyebrow mb-3">Kontakt</p>
          <a href={company.phoneHref} data-testid="contact-phone-link" className="block text-[20px] md:text-[22px] font-medium tracking-[-0.01em] hover:text-[color:var(--violet)] transition-colors">{company.phone}</a>
          {company.confirmed.email && (
            <a href={`mailto:${company.email}`} data-testid="contact-email-link" className="block mt-1 text-[color:var(--ink-2)] hover:text-[color:var(--ink)] transition-colors link-underline">{company.email}</a>
          )}
          <a href={company.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="block mt-1 text-[color:var(--ink-2)] hover:text-[color:var(--ink)] transition-colors link-underline">WhatsApp</a>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="eyebrow mb-3">Termine</p>
          <p className="text-[color:var(--ink-2)]" data-testid="contact-hours-note">{company.hoursNote}</p>
        </FadeUp>
        {company.confirmed.address && (
          <FadeUp delay={0.15}>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border hairline bg-[color:var(--surface)]">
              <iframe title={`Standort ${company.name}`} src={mapSrc} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="map-frame absolute inset-0 h-full w-full border-0" data-testid="contact-map" />
            </div>
          </FadeUp>
        )}
      </div>
      <FadeUp delay={0.1} className="lg:col-span-7">
        <ContactForm />
      </FadeUp>
    </div>
  </section>
);
